import React, { useEffect, useState } from 'react';
import {
	Drawer,
	IconButton,
	Typography,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Collapse,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { FluxIcon } from '@meltwater/flux-web-components-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { getMenuConfiguration } from '../../config/menuConfig';
import { Tooltip } from '@mui/material';
import styles from './SidePanel.module.css';

const PANEL_STATE_KEY = 'gsd-side-panel-open';
const EXPANDED_SECTION_KEY = 'gsd-side-panel-expanded-section';

const SidePanel = () => {
	const [open, setOpen] = useState(() => {
		return localStorage.getItem(PANEL_STATE_KEY) === 'true';
	});
	const [expandedSection, setExpandedSection] = useState(() => {
		return localStorage.getItem(EXPANDED_SECTION_KEY) || 'Meltwater Tools';
	});
	const [permissions, setPermissions] = useState([]);
	const [sendgridpermissions, setSendgridpermissions] = useState([]);
	const [dashboardpermissions, setDashboardpermissions] = useState([]);
	const [twitterxpermissions, setTwitterxpermissions] = useState([]);
	const [entitlementPermissions, setEntitlementPermissions] = useState([]);
	const [intercomPermissions, setIntercomPermissions] = useState([]);
	const [mediarelationspermissions, setMediarelationspermissions] = useState([]);
	const [companiespermissions, setCompaniespermissions] = useState([]);
	const [groups, setGroups] = useState([]);
	const navigate = useNavigate();
	const location = useLocation();

	// Hide panel on home page
	const shouldShowPanel = !['//', '/', '/home'].includes(location.pathname);

	useEffect(() => {
		localStorage.setItem(PANEL_STATE_KEY, open);
	}, [open]);

	useEffect(() => {
		if (expandedSection) {
			localStorage.setItem(EXPANDED_SECTION_KEY, expandedSection);
		}
	}, [expandedSection]);

	useEffect(() => {
		const jwtToken = getJwtToken();
		if (jwtToken) {
			const {
				permissions,
				sendgridpermissions,
				dashboardpermissions,
				twitterxpermissions,
				entitlementPermissions,
				intercomPermissions,
				mediarelationspermissions,
				companiespermissions,
				groups,
			} = jwt_decode(jwtToken);
			setPermissions(permissions || 'none');
			setSendgridpermissions(sendgridpermissions || 'none');
			setDashboardpermissions(dashboardpermissions || 'none');
			setTwitterxpermissions(twitterxpermissions || 'none');
			setEntitlementPermissions(entitlementPermissions || 'none');
			setIntercomPermissions(intercomPermissions || 'none');
			setMediarelationspermissions(mediarelationspermissions || 'none');
			setCompaniespermissions(companiespermissions || 'none');
			setGroups(groups || []);
		}
	}, []);

	const { AdminTools, objExplorer, mwTools, IST_Tools } = getMenuConfiguration(
		permissions,
		sendgridpermissions,
		dashboardpermissions,
		twitterxpermissions,
		entitlementPermissions,
		intercomPermissions,
		mediarelationspermissions,
		companiespermissions,
		[], // auth0permissions (not fetched in this component)
		groups
	);

	const handleDrawerToggle = () => {
		setOpen(!open);
	};

	const handleSectionClick = (section) => {
		setExpandedSection(expandedSection === section ? null : section);
	};

	const handleMenuItemClick = (href, target) => {
		if (target === '_blank') {
			window.open(href, '_blank');
		} else {
			navigate(href);
		}
	};

	const renderSection = (title, items) => {
		const filteredItems = items.filter((item) => item.show !== false);

		if (filteredItems.length === 0) {
			return null;
		}

		const isExpanded = expandedSection === title;

		return (
			<React.Fragment key={title}>
				<ListItem
					button
					onClick={() => handleSectionClick(title)}
					className={styles.sectionHeader}
					sx={{
						py: 1.5,
						px: 2,
						margin: '4px 8px',
						borderRadius: '8px',
						backgroundColor: 'rgba(29, 159, 159, 0.08)',
						borderLeft: '4px solid #1d9f9f',
						'&:hover': {
							backgroundColor: 'rgba(29, 159, 159, 0.15)',
						},
					}}
				>
					<ListItemText
						primary={
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 700,
									color: '#1a8d8c',
									fontSize: '0.8rem',
									textTransform: 'uppercase',
									letterSpacing: '0.8px',
								}}
							>
								{open ? title : title.split(' ').map(w => w[0]).join('')}
							</Typography>
						}
					/>
					{open && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
				</ListItem>
				<Collapse in={isExpanded} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						{filteredItems.map((item, index) => (
							<ListItem
								button
								key={index}
								onClick={() => handleMenuItemClick(item.href, item.target)}
								className={styles.menuItem}
								sx={{
									py: 1.2,
									px: open ? 2.5 : 2,
									margin: '2px 8px',
									borderRadius: '6px',
									backgroundColor: '#ffffff',
									transition: 'all 0.15s ease',
									borderLeft: '3px solid transparent',
									position: 'relative',
									overflow: 'hidden',
									'&:hover': {
										backgroundColor: '#f0f9f9',
										borderLeft: '3px solid #188182',
										transform: 'translateX(2px)',
										boxShadow: '0 2px 4px rgba(24, 129, 130, 0.1)',
									},
								}}
							>
								{item.startIcon && (
									<ListItemIcon sx={{ minWidth: open ? 40 : 'auto', color: '#188182' }}>
										<FluxIcon name={item.startIcon} size="small" />
									</ListItemIcon>
								)}
								{open && (
									<ListItemText
										primary={item.title}
										secondary={item.text}
										primaryTypographyProps={{
											variant: 'body2',
											sx: { fontWeight: 500, color: '#202124', fontSize: '0.875rem' },
										}}
										secondaryTypographyProps={{
											variant: 'caption',
											sx: { color: '#5f6368', fontSize: '0.75rem', marginTop: '2px' },
										}}
									/>
								)}
							</ListItem>
						))}
					</List>
				</Collapse>
			</React.Fragment>
		);
	};

	if (!shouldShowPanel) {
		return null;
	}

	return (
		<>
			<Drawer
				variant="persistent"
				anchor="left"
				open={open}
				className={styles.drawer}
				sx={{
					width: open ? 280 : 0,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: 280,
						boxSizing: 'border-box',
						transition: 'transform 0.3s ease',
						borderRight: 'none',
						background: 'linear-gradient(135deg, #ffffff 0%, #f0f9f9 100%)',
						overflowX: 'hidden',
						top: '64px',
						height: 'calc(100vh - 64px)',
						zIndex: 1200,
						borderTopRightRadius: '16px',
						borderBottomRightRadius: '16px',
						boxShadow: '2px 0 12px rgba(29, 159, 159, 0.15)',
					},
				}}
			>
				<List sx={{ pt: 1 }}>
					{renderSection('Meltwater Tools', mwTools)}
					{renderSection('Internal Support Tools', IST_Tools)}
					{renderSection('MW Object Explorer', objExplorer)}
					{permissions.includes('admin') && renderSection('Admin Tools', AdminTools)}
				</List>
			</Drawer>

			{open && (
				<Tooltip
					title="Hide navigation panel"
					placement="right"
					arrow
				>
					<IconButton
						aria-label="close navigation"
						onClick={handleDrawerToggle}
						aria-expanded="true"
						sx={{
							position: 'fixed',
							left: 290,
							top: 140,
							backgroundColor: '#ffffff',
							color: '#188182',
							boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
							opacity: 0.8,
							transition: 'all 0.3s ease-in-out',
							borderRadius: '50%',
							width: 36,
							height: 36,
							'&:hover': {
								opacity: 1,
								backgroundColor: '#f5f5f5',
								transform: 'translateX(-3px)',
							},
							zIndex: 1100,
						}}
					>
						<ChevronLeftIcon />
					</IconButton>
				</Tooltip>
			)}

			{!open && (
				<Tooltip
					title="Show navigation panel"
					placement="right"
					arrow
				>
					<IconButton
						aria-label="open navigation"
						onClick={handleDrawerToggle}
						aria-expanded="false"
						sx={{
							position: 'fixed',
							left: 0,
							top: 140,
							backgroundColor: '#188182',
							color: 'white',
							borderRadius: '0 50% 50% 0',
							padding: '8px 6px 8px 0',
							width: 25,
							height: 56,
							minWidth: 25,
							boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
							'&:hover': {
								backgroundColor: '#136e6f',
								transform: 'translateX(3px)',
							},
							zIndex: 1100,
							transition: 'all 0.2s ease-in-out',
						}}
					>
						<ChevronRightIcon />
					</IconButton>
				</Tooltip>
			)}
		</>
	);
};

export default SidePanel;
